import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Icon from 'icons/Icon'
import { closeModal, openModal } from 'store/redux-slices/modal'
import { MODALS } from 'constants/modals'
import LoginModalForm from './components/LoginModalForm'
import SignupModalForm from './components/SignupModalForm'
import ForgotPasswordForm from './components/ForgotPasswordForm'
import './styles/index.scss'
import { useLocation, useNavigate } from 'react-router-dom'
import { AUTH_MODAL_TABS } from 'constants/general'
import Banner from 'components/Banner'
import ConfirmSignupCancellation from 'components/ConfirmSignupCancellation'

const AuthModal = (props) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [authTab, setAuthTab] = useState(props?.currentTab || AUTH_MODAL_TABS.LOGIN)
  const { isEmailSent } = useSelector((state) => state.login)
  const { languageData } = useSelector((state) => state.language)
  const showSignupCancellationModal = useSelector((state) => {
    const flag = state?.uiConfig?.general?.showSignupCancellationModal
    return flag === true || flag === 'true'
  })

  const setAuthQueryParam = useCallback((tab) => {
    const params = new URLSearchParams(location.search)
    params.set('auth-modal', tab)
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true })
  }, [location.search, location.pathname, navigate])

  const closeAuthModal = useCallback(() => {
    const next = new URLSearchParams(location.search)
    next.delete('auth-modal')
    navigate({ pathname: location.pathname, search: next.toString() }, { replace: true })
    dispatch(closeModal(MODALS.AUTH))
    if (authTab === AUTH_MODAL_TABS.SIGNUP && showSignupCancellationModal) {
      dispatch(openModal({
        name: MODALS.CANCEL_SIGNUP_CONFIRMATION_MODAL,
        body: (
          <ConfirmSignupCancellation
            onPrimaryClick={() => {
              setAuthQueryParam(AUTH_MODAL_TABS.SIGNUP)
              dispatch(closeModal(MODALS.CANCEL_SIGNUP_CONFIRMATION_MODAL))
            }}
            onSecondaryClick={() => {
              closeAuthModal()
              dispatch(closeModal(MODALS.CANCEL_SIGNUP_CONFIRMATION_MODAL))
            }}
          />
        ),
        onClose: () => {
          dispatch(closeModal(MODALS.CANCEL_SIGNUP_CONFIRMATION_MODAL))
        },
        modalProps: {
          className: 'cancel-signup-modal-wrapper'
        }
      }))
    }
  }, [location.search, location.pathname, navigate, dispatch, authTab, setAuthQueryParam, showSignupCancellationModal])

  // Sync URL query with tab changes
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const hasAuthModalParam = params.has('auth-modal')

    if (!hasAuthModalParam) {
      return
    }

    if (params.get('auth-modal') !== authTab) {
      params.set('auth-modal', authTab)
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true })
    }
  }, [authTab, location.search, navigate, location.pathname])

  // Ensure ESC key clears query param and closes modal to prevent reopen
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        event.preventDefault()
        closeAuthModal()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [closeAuthModal])

  return (
    <div className={`auth-modal ${authTab} ${isEmailSent ? 'mail-sent' : ''}`}>
      <div className='logo'>
        <Icon name='logo' width='133' height='26' />
      </div>

      <Icon
        name='xmark'
        className='close-icon'
        onClick={() => closeAuthModal()}
      />

      <div className='tabs'>
        <div
          className={authTab === AUTH_MODAL_TABS.SIGNUP ? 'tab active' : 'tab'}
          onClick={() => setAuthTab(AUTH_MODAL_TABS.SIGNUP)}
        >
          {languageData?.signup || 'Signup'}
        </div>
        <div
          className={authTab === AUTH_MODAL_TABS.LOGIN ? 'tab active' : 'tab'}
          onClick={() => setAuthTab(AUTH_MODAL_TABS.LOGIN)}
        >
          {languageData?.login || 'Login'}
        </div>
      </div>

      <div className='banner-container'>
        <div className='wrapper-content banner'>
          <Banner pageType='auth' />
        </div>
      </div>

      <div className='auth-modal-body'>
        {authTab === AUTH_MODAL_TABS.LOGIN && <LoginModalForm setAuthTab={setAuthTab} />}
        {authTab === AUTH_MODAL_TABS.SIGNUP && <SignupModalForm setAuthTab={setAuthTab} />}
        {authTab === AUTH_MODAL_TABS.FORGOT_PASSWORD && <ForgotPasswordForm setAuthTab={setAuthTab} />}
      </div>
    </div>
  )
}

export default AuthModal
